/**
 * H9 / M26 – one error-state pattern for data that failed to load.
 *
 * A failed call must never look like an empty result ("No active POs for this vendor" when the
 * request actually returned 500, or an empty vendor dropdown when it returned 403). Callers keep
 * the failure in state and render the message from here instead of an empty list.
 *
 * Messages are generic on purpose: the server already logs the detail (and strips it from the
 * response), so nothing from the error body is shown.
 */

export type LoadErrorKind = 'forbidden' | 'error';

export interface LoadError {
  kind: LoadErrorKind;
  message: string;
}

function statusOf(error: unknown): number | undefined {
  const e = error as any;
  return e?.response?.status ?? e?.status ?? e?.statusCode;
}

/**
 * @param label plural noun for what was being loaded, e.g. "purchase orders", "vendors".
 */
export function describeLoadError(error: unknown, label: string): LoadError {
  const status = statusOf(error);
  if (status === 401 || status === 403) {
    return { kind: 'forbidden', message: `You don't have access to ${label} – contact your administrator.` };
  }
  return { kind: 'error', message: `Could not load ${label} – try again or contact support.` };
}

/**
 * The message to show for a failed mutation (save, submit, approve). A 4xx body's `error` is a
 * user-facing message written by the API (validation, conflict), so it is shown as-is; anything
 * else gets the generic text.
 */
export function describeActionError(error: unknown, fallback = 'Something went wrong – try again or contact support.'): string {
  const status = statusOf(error);
  const body = (error as any)?.response?.data;
  if (status === 403) return "You don't have permission to do that – contact your administrator.";
  if (status && status >= 400 && status < 500 && typeof body?.error === 'string' && body.error.trim()) {
    return body.error;
  }
  return fallback;
}
